import {
  FacebookLoginProvider,
  SocialAuthService,
  SocialUser,
} from '@abacritt/angularx-social-login';
import { Component, Input, Injector, NgZone, OnInit, signal } from '@angular/core';
import {
  ApiError,
  ApiSuccessfulLogin,
  GenericApiResponse,
  GoogleUser,
} from 'src/app/interfaces/api';
import { Router } from '@angular/router';
import environment from 'src/environments/environment';
import { UserService } from 'src/app/services/user.service';
import { AppleLoginService } from 'src/app/services/apple-login.service';
import jwt_decode from 'jwt-decode';
import { NgIf } from '@angular/common';
import { User } from '../../models/user.model';
import { IUpdateExistingAccountData } from '../login-modal/login-modal.component';
import { AnalyticsService } from '../../../services/analytics.service';
import { AuthService } from '../../../services/auth.service';
import { ErrorBlockComponent } from '../error-block/error-block.component';

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
  };
  user:
    | undefined
    | {
        email: string;
        name: {
          firstName: string;
          lastName: string;
        };
      };
}

interface AppleJWT {
  iss: string;
  aud: string;
  exp: number;
  iat: number;
  sub: string;
  c_hash: string;
  email: string;
  email_verified: boolean;
  is_private_email: boolean;
  auth_time: number;
  nonce_supported: boolean;
}

declare let google: any;

@Component({
  selector: 'app-social-login-buttons',
  templateUrl: './social-login-buttons.component.html',
  styleUrls: ['./social-login-buttons.component.scss'],
  imports: [NgIf, ErrorBlockComponent],
  standalone: true
})

/**
 * @isUnihomesDeepLink Only pass true when you want the user to be re-directed to the registration page
 */
export class SocialLoginButtonsComponent implements OnInit {
  @Input() isUnihomesDeeplink: boolean = false;

  @Input() public universityUUID: string = '';

  @Input() public accommodationUUID: string = '';

  @Input() loginModal: boolean = false;

  @Input() updateExistingAccountData!: IUpdateExistingAccountData | null;

  public loadingSocial = signal(false);

  public googleClient: any;

  public unknownError = signal(false);

  public unknownErrorMessage = signal(
    'There was an unknown error logging you in.'
  );

  // Lazy-loaded SocialAuthService to avoid circular dependency
  private socialAuthServiceInstance: SocialAuthService | null = null;




  constructor(
    public readonly auth: AuthService,
    public readonly user: UserService,
    private readonly injector: Injector,
    public readonly apple: AppleLoginService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly analyticsService: AnalyticsService,
    private readonly socAuth: SocialAuthService
  ) {}

  ngOnInit() {
    if (!this.googleClient) this.initGoogle();
  }

  private initGoogle(): void {
    // sometimes the google library can be a little slow to load.
    // so if it's not loaded yet, wait until it is and then re-run
    if (!google) {
      // @ts-ignore
      window.onGoogleLibraryLoad = () => {
        this.initGoogle();
      };
    } else {
      // eslint-disable-next-line
      this.googleClient = google.accounts.oauth2.initTokenClient({
        client_id: environment.google.client_id,
        callback: this.handleGoogleCredentialResponse.bind(this),
        scope:
          'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
      });
    }
  }

  handleGoogleCredentialResponse(response: { access_token: string }): void {
    try {
      this.analyticsService.trackEvent('social_start', {
        universityUUID: this.universityUUID ?? '',
        accommodationUUID: this.accommodationUUID ?? '',
        source: 'WEB',
        provider: 'google',
      });
      const googleUser = new SocialUser();
      googleUser.authToken = response.access_token;
      this.user.getGoogleUserDetails(response.access_token).subscribe({
        next: (res) => {
          const resUser = <GoogleUser>res;
          googleUser.email = resUser.email;
          googleUser.firstName = resUser.given_name;
          googleUser.lastName = resUser.family_name;
          this.ngZone.run(() => {
            this.handleSocialLogin(googleUser, 'GOOGLE');
          });
        },
        error: () => {
          this.setUnknownError(
            'There was a problem communicating with Google to retrieve your details.'
          );
        },
      });
    } catch (e) {
      this.setUnknownError(
        'There was a problem with the Google Sign In process.'
      );
    }
  }

  public appleSignIn(): void {
    if (!this.loadingSocial()) {
      this.analyticsService.trackEvent('social_start', {
        universityUUID: this.universityUUID ?? '',
        accommodationUUID: this.accommodationUUID ?? '',
        source: 'WEB',
        provider: 'apple',
      });
      this.loadingSocial.set(true);
      this.apple
        .signIn()
        .then((response: AppleSignInResponse) => {
          if (response.authorization.id_token) {
            const appleUser = new SocialUser();
            appleUser.authToken = response.authorization.id_token;

            const decodedToken = jwt_decode<AppleJWT>(
              response.authorization.id_token
            );

            appleUser.email = decodedToken.email;

            if (response.user) {
              appleUser.firstName = response.user.name.firstName;
              appleUser.lastName = response.user.name.lastName;
            }

            this.handleSocialLogin(appleUser, 'APPLE');
          }
        })
        .catch(() => {
          this.loadingSocial.set(false);
          this.setUnknownError(
            'There was a problem with the Apple Sign In process.'
          );
        });
    }
  }

  public facebookSignIn(): void {
    // if a load is currently ongoing, don't allow another
    if (!this.loadingSocial()) {
      this.analyticsService.trackEvent('social_start', {
        universityUUID: this.universityUUID ?? '',
        accommodationUUID: this.accommodationUUID ?? '',
        source: 'WEB',
        provider: 'facebook',
      });
      this.loadingSocial.set(true);
      this.socAuth
        .signIn(FacebookLoginProvider.PROVIDER_ID)
        .then((socialUserData) =>
          this.handleSocialLogin(socialUserData, 'FACEBOOK')
        )
        .catch(() => {
          this.loadingSocial.set(false);
          this.setUnknownError(
            'There was a problem with the Facebook Login process.'
          );
        });
    }
  }

  private handleSocialLogin(
    socialUserData: SocialUser,
    platform: string
  ): void {
    if (socialUserData.authToken && this.auth.csrfLoaded) {
      this.auth
        .socialLogin(
          socialUserData.authToken,
          platform,
          this.isUnihomesDeeplink
        )
        .subscribe({
          next: (user) => {
            if (this.updateExistingAccountData) {
              this.updateExistingUserDetails(user as ApiSuccessfulLogin);
              return;
            }
            this.loginAndNavigate(user as ApiSuccessfulLogin);
          },
          error: (err: ApiError) => {
            // 401 means no user found, so they must be using social to register
            if (err.status === 401) {
              this.handleSocialRegister(socialUserData, platform);
            } else if (err.status === 310) {
              // this redirects to the register page with the pre-populated uni homes fields
              this.handleSocialRegister(socialUserData, platform);
            } else {
              this.setUnknownError(` ${String(err.error?.message ?? '')}`);
            }
            this.loadingSocial.set(false);
          },
        });
    } else {
      this.setUnknownError('There was an error logging you in.');
    }
    this.loadingSocial.set(false);
  }

  /**
   * Update existing user account details request
   *
   * @param ApiSuccessfulLogin authenticatedUser
   * @returns void
   */
  public updateExistingUserDetails(
    authenticatedUser: ApiSuccessfulLogin
  ): void {
    const tempUser = structuredClone(authenticatedUser.data.user);
    tempUser.uuid = authenticatedUser.data.user.uuid;
    if (this.updateExistingAccountData) {
      tempUser.postcode = this.updateExistingAccountData.postcode;
      tempUser.universityId = this.updateExistingAccountData.universityId;
      tempUser.accommodationId = this.updateExistingAccountData.accommodationId;
    }

    this.user
      .updateUser(tempUser)
      .subscribe((data: GenericApiResponse<User>) => {
        const user = {
          ...authenticatedUser,
          data: { user: data.data, token: authenticatedUser.data.token },
        } as ApiSuccessfulLogin;

        this.loginAndNavigate(user);
      });
  }

  /**
   * Login and redirect user on successful login;
   * @param authenticatedUserResponse
   */
  public loginAndNavigate(authenticatedUserResponse: ApiSuccessfulLogin): void {
    this.auth.login(authenticatedUserResponse);
    const route = this.auth.redirectAfterLogin ?? '/app';
    this.router.navigate([route]).finally(() => false);
  }

  /*
   * Handle social registration
   * @param socialUserData
   * @param platform
   * @returns void
   */
  public handleSocialRegister(
    socialUserData: SocialUser,
    platform: string
  ): void {
    this.auth.socialUser = User.fromSocialUser(socialUserData, platform);

    // store some thing in session in case the page gets refreshed
    this.auth.storeSocialUserInSession();

    this.auth.logoutReason = 0; // reset this, the error interceptor will have set it to 401

    this.router.navigate(['/register']).finally(() => false);
  }

  private setUnknownError(message: string): void {
    this.unknownError.set(true);
    this.unknownErrorMessage.set(message);
  }
}
